import type { CourseTee } from "../codegen/types";

/**
 * The tee attributes the stroke maths reads. Takes the generated GraphQL
 * CourseTee type — entity instances on the API satisfy it by duck typing.
 */
export type StrokeTee = Pick<CourseTee, "slope" | "courseRating" | "par">;

/**
 * WHS derives a stroke allowance in two named steps, and the event's
 * `hcpFactor` is the handicap allowance of the second one:
 *
 *   Course Handicap  = round(index × slope/113 + (courseRating − par))
 *   Playing Handicap = round(Course Handicap × allowance)
 *
 * Each step is exported so a caller can take the one it needs. POL-67 splits an
 * allowance across three 6-hole blocks and needs the unrounded value: rounding
 * ahead of a distribution compounds.
 */
export const getCourseHandicapExact = (
  hcp: number,
  courseTee: StrokeTee,
): number =>
  hcp * (courseTee.slope / 113) + (courseTee.courseRating - courseTee.par);

/**
 * `Math.round(-0.4)` is `-0`, which a plus player reaches whenever their
 * allowance rounds away. It prints as "0" but is not `Object.is` equal to it.
 */
const roundStrokes = (value: number): number => {
  const rounded = Math.round(value);
  return rounded === 0 ? 0 : rounded;
};

export const getCourseHandicap = (hcp: number, courseTee: StrokeTee): number =>
  roundStrokes(getCourseHandicapExact(hcp, courseTee));

export const getPlayingHandicap = (
  courseHandicap: number,
  allowance: number,
): number => roundStrokes(courseHandicap * allowance);

/**
 * The strokes a player receives for the round — a Course Handicap at an
 * allowance of 1, a Playing Handicap below it.
 */
export const getCourseStrokes = (
  hcp: number,
  courseTee: StrokeTee,
  hcpFactor: number,
): number => getPlayingHandicap(getCourseHandicap(hcp, courseTee), hcpFactor);

/** A full card. The hole count stroke allocation assumes unless told otherwise. */
export const HOLE_COUNT = 18;

/**
 * Distribute total stroke allowance across the holes in play, by stroke index.
 *
 * `holeIndex` is the hole's rank within those holes, 1..`holeCount`
 * (1 = hardest) — the course stroke index when the whole card is in play, a
 * rank derived from it when fewer holes are. Positive `strokes`: extra stroke
 * on the `strokes % holeCount` hardest holes. Negative `strokes` (plus
 * handicaps): subtract a stroke on the `strokes % holeCount` easiest holes.
 */
export const getHoleStrokes = (
  strokes: number,
  holeIndex: number,
  holeCount = HOLE_COUNT,
): number => {
  const base =
    strokes >= 0
      ? Math.floor(strokes / holeCount)
      : Math.ceil(strokes / holeCount);
  const rem = Math.abs(strokes) % holeCount;
  if (strokes >= 0) {
    return base + (holeIndex <= rem ? 1 : 0);
  }
  return base - (holeIndex > holeCount - rem ? 1 : 0);
};
